import { useState } from "react";
import axios from "axios";
import { Logo } from "@/components/site/Logo";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const LABELS = { home: "Home page card", nav: "Top menu button", hero: "Opening section", ritual: "How it works", featured: "Featured product", offer: "Offer section", "pack-1": "1-bottle package", "pack-3": "3-bottle package", "pack-6": "6-bottle package", footer: "Footer" };
const NAMES = { "joint-genesis": "Joint Genesis", prostavive: "ProstaVive", "nitric-boost": "Nitric Boost" };

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-[#F2EDE4]/10 bg-[#0F2822] p-5" data-testid={testId}>
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E5A93C]">{label}</p>
    <p className="mt-2 font-display text-4xl font-bold text-[#F2EDE4]">{value}</p>
  </div>
);

const ProductStats = ({ prod }) => {
  const rows = [...(prod?.placements ?? [])].sort((a, b) => b.clicks - a.clicks);
  const max = Math.max(1, ...rows.map((r) => r.clicks));
  return (
    <section className="mt-12" data-testid={`stats-product-${prod.product}`}>
      <div className="flex items-baseline justify-between border-b border-[#F2EDE4]/10 pb-3">
        <h2 className="font-display text-2xl font-bold">{NAMES[prod.product] ?? prod.product}</h2>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#E5A93C]" data-testid={`stats-product-total-${prod.product}`}>{prod.total_clicks ?? 0} clicks</p>
      </div>
      <div className="mt-6 space-y-5">
        {rows.map((r, i) => (
          <div key={r.placement} data-testid={`stats-row-${prod.product}-${r.placement}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4">
              <p className="font-display text-lg font-semibold">{i === 0 && r.clicks > 0 ? "★ " : ""}{LABELS[r.placement] ?? r.placement}</p>
              <p className="text-sm text-[#F2EDE4]/75">{r.clicks} clicks · {r.unique_visitors} visitors · {r.last_24h} today</p>
            </div>
            <div className="mt-2 h-3 overflow-hidden rounded-full bg-[#F2EDE4]/10">
              <div className="h-full rounded-full bg-[#D96B43] transition-[width] duration-700" style={{ width: `${(r.clicks / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Stats() {
  const [key, setKey] = useState(() => sessionStorage.getItem("gb_stats_key") ?? "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async (e) => {
    e?.preventDefault();
    if (!API) return;
    setError("");
    try {
      const res = await axios.get(`${API}/clicks/stats`, { headers: { "X-Stats-Key": key } });
      sessionStorage.setItem("gb_stats_key", key);
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err?.response?.status === 401 ? "Wrong access key." : "Could not load stats.");
    }
  };

  const products = Array.isArray(data?.products) ? data.products : [];

  return (
    <main className="min-h-screen bg-[#091D18] px-5 py-10 text-[#F2EDE4] sm:px-10" data-testid="stats-page">
      <div className="mx-auto max-w-4xl">
        <Logo testId="stats-logo" />
        <h1 className="mt-10 font-display text-4xl font-bold tracking-tight sm:text-5xl">Buy-button clicks</h1>
        <form onSubmit={load} className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="stats-key-form">
          <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Access key" required
            data-testid="stats-key-input"
            className="h-12 flex-1 rounded-full border border-[#F2EDE4]/20 bg-[#F2EDE4]/5 px-5 outline-none focus:border-[#D96B43]" />
          <button type="submit" data-testid="stats-load-button" className="h-12 rounded-full bg-[#D96B43] px-7 font-semibold text-[#091D18] hover:bg-[#F2EDE4]">
            {data ? "Refresh" : "Show stats"}
          </button>
        </form>
        {error && <p className="mt-4 text-[#D96B43]" data-testid="stats-error">{error}</p>}
        {data && (
          <>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard label="Total clicks" value={data?.total_clicks ?? 0} testId="stats-total-clicks" />
              <StatCard label="Unique visitors who clicked" value={data?.total_unique_visitors ?? 0} testId="stats-unique-visitors" />
              <StatCard label="Email signups" value={data?.leads ?? 0} testId="stats-leads" />
            </div>
            {products.map((prod) => <ProductStats key={prod.product} prod={prod} />)}
          </>
        )}
      </div>
    </main>
  );
}
